/**
 * Sentry Configuration for Frontend Error Tracking
 * 
 * Phase: Pré-Beta - Monitoring Integration
 * Date: 2026-12-19
 * 
 * This module initializes Sentry for React error tracking and performance monitoring.
 * Sentry DSN is configured via environment variable (REACT_APP_SENTRY_DSN).
 * 
 * Features:
 * - Error tracking with stack traces
 * - Performance monitoring (10% sample rate)
 * - User feedback integration
 * - Replay session recording (on errors)
 */

import * as Sentry from "@sentry/react";

/**
 * Initialize Sentry for frontend monitoring
 * 
 * Only activates if REACT_APP_SENTRY_DSN is present in environment.
 * Safe to call in development (will not initialize without DSN).
 */
export function initSentry() {
  const sentryDsn = process.env.REACT_APP_SENTRY_DSN;
  const environment = process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV || 'development';

  // Only initialize if DSN is configured
  if (!sentryDsn) {
    console.warn('⚠️  Sentry DSN not configured - Error tracking disabled');
    return;
  }

  Sentry.init({
    dsn: sentryDsn,
    environment: environment,
    
    // Performance Monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Replay is only recorded when an error occurs
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance Monitoring
    tracesSampleRate: 0.1, // 10% of transactions for performance monitoring
    
    // Session Replay (only on errors)
    replaysSessionSampleRate: 0.0, // Do not record normal sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Release tracking
    release: `nexora-frontend@${process.env.REACT_APP_VERSION || 'unknown'}`,

    // Do not send PII (Personally Identifiable Information)
    sendDefaultPii: false,

    // Ignore specific errors (common false positives)
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'chrome-extension://',
      'moz-extension://',
      // Network errors
      'Network request failed',
      'NetworkError',
      // ResizeObserver (harmless loop)
      'ResizeObserver loop limit exceeded',
    ],

    // Filter transactions
    beforeSend(event, hint) {
      // Filter out debug-level events
      if (event.level === 'debug') {
        return null;
      }
      return event;
    },

    // Custom tags
    initialScope: {
      tags: {
        "app.component": "frontend",
      },
    },
  });

  console.log(`✅ Sentry initialized for environment: ${environment}`);
}

/**
 * Capture a custom error with additional context
 * 
 * @param {Error} error - Error object to capture
 * @param {Object} context - Additional context (user, tags, etc.)
 */
export function captureError(error, context = {}) {
  if (Sentry.getClient()) {
    Sentry.captureException(error, {
      contexts: context,
    });
  } else {
    console.error('Sentry not initialized, logging error locally:', error);
  }
}

/**
 * Set user context for error tracking
 * 
 * @param {Object} user - User object with id, email, etc.
 */
export function setUser(user) {
  if (Sentry.getClient()) {
    Sentry.setUser(user ? {
      id: user.id,
      email: user.email,
      // Do not send sensitive data
    } : null);
  }
}

/**
 * Add breadcrumb for debugging context
 * 
 * @param {string} message - Breadcrumb message
 * @param {Object} data - Additional data
 */
export function addBreadcrumb(message, data = {}) {
  if (Sentry.getClient()) {
    Sentry.addBreadcrumb({
      message,
      data,
      level: 'info',
    });
  }
}

export default Sentry;
